/**
 * Coupon engine: cart drawer, /cart page and /pages/coupons.
 * Ported from the House of Veda theme (same fork, assets/hov-coupons.js at a7e029c).
 *
 * Replaces the old client-side simulation in snippets/minicart.liquid, which compared the typed
 * code against one theme setting (SHUDHI10), computed a percent discount in JS, kept it in
 * sessionStorage and only became real via ?discount= on the checkout link.
 *
 * The cart itself is the source of truth. POST /cart/update.js {discount} applies the code
 * server-side (Shopify validates it, its rules and customer eligibility), and checkout inherits it
 * from the cart. No sessionStorage anywhere.
 *
 * Shudhi deltas from HOV:
 *   - Shiprocket checkout reads ONLY the JS-readable `discount_code` cookie (never the cart's
 *     codes), so syncCookie() mirrors the cart's applicable code into it on every render.
 *   - Single-code mode, because that cookie carries one code: a new code replaces the old one,
 *     and the previous code is restored if Shopify rejects the new one.
 *   - Applied state is shown inline on the ticket; the old "Coupon Applied!" modal and its
 *     confetti are gone (brand rule: calm, no confetti).
 *   - Money is always formatted with the shop's money format; no hard-coded currency glyph.
 *
 * Catalog comes from #hov-coupon-catalog, already filtered server-side by
 * snippets/hov-coupon-catalog.liquid: whatever is in it, this visitor is allowed to see.
 */
(function () {
  'use strict';

  var CART_UPDATE = '/cart/update.js';
  var CART_GET = '/cart.js';
  var COOKIE = 'discount_code';

  // Shopify collapses every rejection reason (bad code, expired, usage exhausted, wrong customer)
  // into a single applicable:false, deliberately, to stop brute-force enumeration. So there is
  // exactly one honest message for all of them. Don't guess at a specific reason.
  var ERR_GENERIC = 'This code can’t be applied to your cart.';
  var ERR_SIGN_IN = ' If this code was sent to you personally, sign in and try again.';
  var ERR_NETWORK = 'Couldn’t reach the store. Please try again.';
  var ERR_EMPTY = 'Your cart is empty. Add an item first.';
  var ERR_TOO_MANY = 'Too many attempts. Please wait a moment.';
  var MSG_SHIPPING = 'Shipping discounts are shown at checkout after adding an address.';

  var MAX_FAILURES = 10;
  var COOLDOWN_MS = 30000;

  // How many tickets the cart drawer shows. The catalog is sorted by `sort` below, so this is
  // "the merchant's top N", and "View all coupons" carries the rest.
  var DRAWER_CARD_LIMIT = 4;

  var failures = 0;
  var cooldownUntil = 0;
  var inFlight = false;
  var lastCart = null;

  // ── Catalog ──────────────────────────────────────────────────────────────────────────────
  var catalogEl = document.getElementById('hov-coupon-catalog');
  var catalog = (function () {
    if (!catalogEl) return [];
    try {
      var list = JSON.parse(catalogEl.textContent || '[]');
      if (!Array.isArray(list)) return [];
      return list.sort(function (a, b) { return (a.sort || 0) - (b.sort || 0); });
    } catch (e) {
      return [];
    }
  })();
  var isGuest = !catalogEl || catalogEl.getAttribute('data-customer') !== '1';

  function findCoupon(code) {
    var want = String(code || '').trim().toUpperCase();
    for (var i = 0; i < catalog.length; i++) {
      if (catalog[i].code === want) return catalog[i];
    }
    return null;
  }

  // ── Money ────────────────────────────────────────────────────────────────────────────────
  var moneyFormat = (catalogEl && catalogEl.getAttribute('data-money-format')) ||
    (window.ajaxCartConfig && window.ajaxCartConfig.moneyFormat) || '{{amount}}';

  function money(cents) {
    var v = Math.round(cents || 0);
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try { return Shopify.formatMoney(v, moneyFormat); } catch (e) { /* fall through */ }
    }
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, (v / 100).toFixed(2));
  }

  // ── Cart reads ───────────────────────────────────────────────────────────────────────────
  // discount_codes is the ONLY complete list of codes on the cart. Rebuilding the set from
  // cart_level_discount_applications (as Shopify's own Horizon theme does) silently drops
  // free-shipping codes and applicable:false codes.
  function appliedCodes(cart) {
    var out = [];
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      if (codes[i] && codes[i].code) out.push(codes[i].code);
    }
    return out;
  }

  // Codes Shopify currently honours, in the merchant's own casing (the casing Shopify echoes).
  function applicableCodes(cart) {
    var out = [];
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      if (codes[i] && codes[i].code && codes[i].applicable) out.push(codes[i].code);
    }
    return out;
  }

  // Upcased because Shopify echoes back the casing the merchant used when creating the discount,
  // while the catalog is upcased in Liquid. Codes are case-insensitive to Shopify.
  function activeCode(cart) {
    var list = applicableCodes(cart);
    return list.length ? String(list[0]).toUpperCase() : null;
  }

  // Derived from the two figures actually shown, so Subtotal − Discount = Total always reconciles.
  // Summing cart_level_discount_applications would miss product-level codes, and
  // cart.total_discount double-counts line-level discounts that items_subtotal_price already has.
  function cartDiscount(cart) {
    if (!cart) return 0;
    return Math.max(0, (cart.items_subtotal_price || 0) - (cart.total_price || 0));
  }

  // What this one code saved, for the "You saved" line. Falls back to the whole cart discount.
  // /cart.js lists cart-level applications flat ({type, title, total_allocated_amount}), but the
  // /cart/update.js response nests them ({amount, discount_application: {type, title}}); read both.
  function codeSavings(cart, code) {
    var want = String(code || '').toUpperCase();
    var sum = 0;
    var apps = (cart && cart.cart_level_discount_applications) || [];
    for (var i = 0; i < apps.length; i++) {
      var a = apps[i] || {};
      var info = a.discount_application || a;
      var amount = a.discount_application ? a.amount : a.total_allocated_amount;
      if (info.type === 'discount_code' && String(info.title || '').toUpperCase() === want) {
        sum += amount || 0;
      }
    }
    var items = (cart && cart.items) || [];
    for (var j = 0; j < items.length; j++) {
      var allocs = items[j].line_level_discount_allocations || [];
      for (var k = 0; k < allocs.length; k++) {
        var app = allocs[k].discount_application || {};
        if (app.type === 'discount_code' && String(app.title || app.key || '').toUpperCase() === want) {
          sum += allocs[k].amount || 0;
        }
      }
    }
    return sum > 0 ? sum : cartDiscount(cart);
  }

  function getCart() {
    return fetch(CART_GET, { credentials: 'same-origin' }).then(readCart);
  }

  // Shopify answers 200 for almost everything, so a non-cart body is the one failure that would
  // surface as a raw TypeError downstream. Normalise it into a rejection here.
  function readCart(res) {
    if (!res.ok) throw new Error('cart http ' + res.status);
    return res.json().then(function (data) {
      if (!data || typeof data !== 'object' || !('item_count' in data)) throw new Error('not a cart');
      return data;
    });
  }

  // `discount` replaces the WHOLE set of codes on the cart; '' removes them all.
  function postDiscounts(codes) {
    return fetch(CART_UPDATE, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    }).then(readCart);
  }

  // ── Shiprocket hand-off ──────────────────────────────────────────────────────────────────
  // Shiprocket's checkout (snippets/sr-checkout.liquid) pre-applies whatever is in the
  // `discount_code` cookie and ignores the cart's own codes, so the cookie must mirror the cart.
  // Session cookie; re-synced from the cart on every page load.
  function readCookie() {
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      var eq = parts[i].indexOf('=');
      if (parts[i].slice(0, eq) === COOKIE) {
        try { return decodeURIComponent(parts[i].slice(eq + 1)); } catch (e) { return parts[i].slice(eq + 1); }
      }
    }
    return null;
  }

  function writeCookie(code) {
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    var host = location.hostname;
    var base = host.replace(/^www\./, '');
    // Clear host-only and domain-scoped copies first: a /discount/CODE link may have set one on the
    // parent domain, and a leftover copy would bring a removed code back at checkout.
    ['', '; domain=' + host, '; domain=.' + base].forEach(function (d) {
      document.cookie = COOKIE + '=; path=/; max-age=0; SameSite=Lax' + d + secure;
    });
    if (code) document.cookie = COOKIE + '=' + encodeURIComponent(code) + '; path=/; SameSite=Lax' + secure;
  }

  // The cookie mirrors what the drawer shows:
  //   - an applicable code on the cart is written;
  //   - a cart WITH items but no applicable code hands nothing over (e.g. the store's automatic 10%
  //     out-ranks a non-combining code, so Shopify parks the code as applicable:false);
  //   - an EMPTY cart keeps a code still parked on it: a /discount/CODE link sets the cookie and
  //     parks the code there, and Shiprocket's PDP "Buy now" must still see it. Once that code has
  //     left the cart (an explicit Remove, or the fresh cart after an order) the cookie is cleared.
  function syncCookie(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return;
    var code = applicableCodes(cart)[0] || null;
    var current = readCookie();
    if (code) {
      if (current !== code) writeCookie(code);
      return;
    }
    if (!current) return;
    if (cart.item_count > 0) { writeCookie(null); return; }
    var onCart = appliedCodes(cart).some(function (c) { return c.toUpperCase() === current.toUpperCase(); });
    if (!onCart) writeCookie(null);
  }

  // ── Rendering ────────────────────────────────────────────────────────────────────────────
  // Surface-agnostic: every cart surface on the page is driven from one computation.
  function each(sel, fn) {
    var nodes = document.querySelectorAll(sel);
    for (var i = 0; i < nodes.length; i++) fn(nodes[i]);
  }

  function render(cart) {
    lastCart = cart;
    var code = activeCode(cart);
    var discount = cartDiscount(cart);

    each('.hov-cart__coupon-preview', function (p) {
      var input = p.querySelector('[data-coupon-input]');
      var pill = p.querySelector('[data-coupon-applied]');
      var pillCode = p.querySelector('[data-coupon-applied-code]');
      var applyBtn = p.querySelector('[data-coupon-apply]');
      if (input) input.hidden = !!code;
      if (applyBtn) applyBtn.hidden = !!code;
      if (pill) pill.hidden = !code;
      if (code && pillCode) pillCode.textContent = code;
    });

    // Shown for ANY real discount, automatic ones included, not only for an applied code.
    each('[data-cart-discount]', function (row) {
      if (discount > 0) row.removeAttribute('hidden');
      else row.setAttribute('hidden', 'hidden');
    });
    each('[data-cart-discount-amount]', function (el) { el.textContent = '− ' + money(discount); });

    each('[data-coupon-saved-row]', function (row) { row.hidden = !(discount > 0); });
    each('[data-coupon-saved-amount]', function (el) { el.textContent = money(discount); });

    each('[data-cart-subtotal]', function (el) { el.textContent = money(cart.items_subtotal_price); });
    // total_price is already net of every discount, so there is no overlay to apply.
    each('.enj-cartcost', function (el) { el.textContent = money(cart.total_price); });

    renderList(cart);
    syncCookie(cart);
  }

  function conditionState(coupon, cart) {
    if (!coupon) return { ok: true, note: '' };
    var min = Math.round((coupon.minSubtotal || 0) * 100);
    if (min > 0 && (!cart || !cart.item_count)) {
      return { ok: true, note: 'On orders of ' + money(min) + ' or more.' };
    }
    if (min > 0 && cart.items_subtotal_price < min) {
      return { ok: false, note: 'Add ' + money(min - cart.items_subtotal_price) + ' more to use this code.' };
    }
    return { ok: true, note: '' };
  }

  // ── Ticket ───────────────────────────────────────────────────────────────────────────────
  var ICON = {
    copy: 'M9 9h10v10H9zM5 15V5h10',
    ok: 'M5 12.5l4.2 4.2L19 7'
  };

  function svgIcon(d, cls) {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.8');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    if (cls) svg.setAttribute('class', cls);
    var path = document.createElementNS(ns, 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
    return svg;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  // The stub reads as one phrase over three short lines: FLAT / Rs. 50 / OFF.
  // Whole-rupee amounts drop their ".00" so they fit the narrow stub.
  function badgeFor(coupon) {
    var type = (coupon.type || 'percentage').toLowerCase();
    var v = coupon.value || 0;
    if (type === 'free_shipping') return { kind: 'ship', top: '', value: 'Free', bottom: 'Shipping' };
    if (type === 'fixed_amount') {
      return { kind: 'amt', top: 'Flat', value: money(v * 100).replace(/\.00$/, ''), bottom: 'Off' };
    }
    return { kind: 'pct', top: 'Flat', value: v + '%', bottom: 'Off' };
  }

  function couponCard(coupon, cart, currentCode, mode) {
    var applied = currentCode === coupon.code;
    var state = conditionState(coupon, cart);
    var b = badgeFor(coupon);
    var page = mode === 'page';

    var card = el('div', 'hov-offer hov-offer--' + b.kind + (page ? ' hov-offer--page' : '') +
      (applied ? ' is-applied' : '') + (state.ok ? '' : ' is-blocked') + (coupon.private ? ' is-private' : ''));
    card.setAttribute('data-code', coupon.code);

    var badge = el('div', 'hov-offer__badge');
    if (b.top) badge.appendChild(el('span', 'hov-offer__badge-label', b.top));
    badge.appendChild(el('span', 'hov-offer__badge-value' + (b.value.length > 6 ? ' is-long' : ''), b.value));
    badge.appendChild(el('span', 'hov-offer__badge-label', b.bottom));
    card.appendChild(badge);

    var main = el('div', 'hov-offer__main');
    var row = el('div', 'hov-offer__code-row');

    // <code>, not <span>: assets/hov-typography.css sets font-family !important on a selector list
    // that includes span; code is deliberately absent from it.
    row.appendChild(el('code', 'hov-offer__code', coupon.code));

    var copy = el('button', 'hov-offer__copy');
    copy.type = 'button';
    copy.setAttribute('data-coupon-copy', coupon.code);
    copy.setAttribute('aria-label', 'Copy code ' + coupon.code);
    copy.appendChild(svgIcon(ICON.copy, 'hov-offer__copy-ico'));
    copy.appendChild(svgIcon(ICON.ok, 'hov-offer__copy-ok'));
    row.appendChild(copy);

    if (coupon.private) row.appendChild(el('span', 'hov-offer__tag', 'For you'));

    var act = el('button', 'hov-offer__btn');
    act.type = 'button';
    if (applied) {
      var ok = el('span', 'hov-offer__ok');
      ok.appendChild(svgIcon(ICON.ok));
      ok.appendChild(document.createTextNode('Applied'));
      row.appendChild(ok);
      act.className += ' hov-offer__btn--link';
      act.textContent = 'Remove';
      act.setAttribute('data-coupon-remove', coupon.code);
      act.setAttribute('aria-label', 'Remove coupon ' + coupon.code);
    } else {
      act.textContent = page ? 'Apply coupon' : 'Apply';
      act.setAttribute('data-coupon-pick', coupon.code);
      act.setAttribute('aria-label', 'Apply coupon ' + coupon.code);
      act.disabled = !state.ok;
    }
    row.appendChild(act);
    main.appendChild(row);

    if (coupon.heading) main.appendChild(el('p', 'hov-offer__desc', coupon.heading));
    if (page && coupon.subtext) main.appendChild(el('p', 'hov-offer__sub', coupon.subtext));

    if (applied) {
      main.appendChild(el('p', 'hov-offer__saved', 'You saved ' + money(codeSavings(cart, coupon.code))));
    } else if (state.note) {
      main.appendChild(el('p', 'hov-offer__note', state.note));
    }

    if (page) {
      if (coupon.endsAt) main.appendChild(el('p', 'hov-offer__meta', 'Valid till ' + coupon.endsAt));
      var lines = String(coupon.terms || '').split('\n').map(function (l) { return l.trim(); }).filter(Boolean);
      if (lines.length) {
        var terms = el('details', 'hov-offer__terms');
        terms.appendChild(el('summary', null, 'Terms'));
        var ul = el('ul');
        lines.forEach(function (l) { ul.appendChild(el('li', null, l)); });
        terms.appendChild(ul);
        main.appendChild(terms);
      }
    }

    card.appendChild(main);
    return card;
  }

  // Fills content only, never touches host.hidden: on /cart the list starts collapsed behind
  // "More offers" and only [data-coupon-list-toggle] owns that state.
  //   'card'  compact tickets: cart drawer (capped at DRAWER_CARD_LIMIT) and the /cart page
  //   'page'  full tickets:    /pages/coupons, the whole catalog
  // Which cart surface a ticket lives on, so focus can follow it across a rebuild.
  var SURFACES = ['.hov-cart-drawer', '.hov-cart-page__coupon', '.sh-coupons'];
  function surfaceOf(node) {
    for (var i = 0; i < SURFACES.length; i++) if (node.closest(SURFACES[i])) return SURFACES[i];
    return null;
  }

  // Set by a ticket's Apply/Remove click and consumed by the next renderList(). The click's button
  // is destroyed by the repaint (and, for a product-level code, by the whole drawer rebuild), so
  // focus is put back on the same ticket's new action button.
  var pendingFocus = null;

  function renderList(cart) {
    var current = activeCode(cart);
    var empty = !catalog.length;

    each('[data-coupon-list]', function (host) {
      host.innerHTML = '';
      if (empty) return;
      var mode = host.getAttribute('data-coupon-list') === 'page' ? 'page' : 'card';
      var max = mode === 'card' && !host.closest('.hov-cart-page__coupon')
        ? Math.min(catalog.length, DRAWER_CARD_LIMIT) : catalog.length;
      for (var i = 0; i < max; i++) host.appendChild(couponCard(catalog[i], cart, current, mode));
    });

    if (pendingFocus) {
      var scope = pendingFocus.surface ? document.querySelector(pendingFocus.surface) : document;
      var cards = scope ? scope.querySelectorAll('.hov-offer') : [];
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].getAttribute('data-code') === pendingFocus.code) {
          var btn = cards[i].querySelector('.hov-offer__btn');
          if (btn && !btn.disabled) btn.focus({ preventScroll: true });
          break;
        }
      }
      pendingFocus = null;
    }

    // Nothing to reveal: hide the toggle rather than offering an empty panel.
    each('[data-coupon-list-toggle]', function (t) { t.hidden = empty; });
    each('[data-coupons-empty]', function (t) { t.hidden = !empty; });
  }

  // ── Messages ─────────────────────────────────────────────────────────────────────────────
  function showError(msg) {
    each('[data-coupon-error]', function (node) {
      node.textContent = msg;
      node.setAttribute('data-visible', 'true');
    });
  }
  function clearError() {
    each('[data-coupon-error]', function (node) { node.removeAttribute('data-visible'); });
  }
  function errGeneric() {
    return ERR_GENERIC + (isGuest ? ERR_SIGN_IN : '');
  }

  // Screen-reader confirmation through ONE page-level live region, so the drawer, /cart and the
  // coupons page never announce the same message twice. Cleared first so a repeat is re-announced.
  var live = null;
  function announce(msg) {
    if (!live) {
      live = document.createElement('p');
      live.className = 'hov-visually-hidden';
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(function () { live.textContent = msg; }, 60);
  }

  // After an apply/remove. An order-level code (SHUDHI50) changes only the totals, which render()
  // repaints in place. A product-level code also changes line prices and items_subtotal_price (the
  // free-shipping basis), so rebuild every cart surface through the theme's own pipeline.
  function paint(cart, prevSubtotal) {
    if (cart.items_subtotal_price !== prevSubtotal && typeof window.renderAllSurfaces === 'function') {
      window.renderAllSurfaces(cart);   // dispatches cart:updated with this cart -> render()
    } else {
      render(cart);
    }
  }

  function setBusy(busy) {
    inFlight = busy;
    each('[data-coupon-apply], [data-coupon-pick], [data-coupon-remove]', function (b) {
      b.disabled = busy;
    });
  }

  // ── Apply / remove ───────────────────────────────────────────────────────────────────────
  function apply(code) {
    var want = String(code || '').trim().toUpperCase();
    if (!want) { showError('Enter a coupon code.'); return; }
    if (inFlight) return;
    if (Date.now() < cooldownUntil) {
      showError(ERR_TOO_MANY);
      if (lastCart) renderList(lastCart);
      return;
    }

    clearError();
    setBusy(true);
    var before = [];
    var prevSubtotal = null;
    var painted = false;

    getCart()
      .then(function (cart) {
        if (!cart.item_count) throw { soft: ERR_EMPTY };

        // The minimum-spend check is the only precise error available, because Shopify refuses to
        // distinguish its own rejection reasons. Catch it before the request.
        var state = conditionState(findCoupon(want), cart);
        if (!state.ok) throw { soft: state.note };

        prevSubtotal = cart.items_subtotal_price;
        before = applicableCodes(cart).filter(function (c) { return c.toUpperCase() !== want; });
        return postDiscounts([want]);
      })
      .then(function (cart) {
        // discount_codes is undocumented (though Shopify's own Horizon theme relies on it). If a
        // store ever answers without it, fall back to the observable effect: did a discount land?
        if (!Array.isArray(cart.discount_codes)) {
          painted = true;
          paint(cart, prevSubtotal);
          if (cartDiscount(cart) > 0) { failures = 0; announce(want + ' applied.'); }
          else showError(errGeneric());
          return;
        }

        var entry = null;
        for (var i = 0; i < cart.discount_codes.length; i++) {
          var dc = cart.discount_codes[i];
          if (dc.code && dc.code.toUpperCase() === want) entry = dc;
        }

        if (!entry || !entry.applicable) {
          failures += 1;
          if (failures >= MAX_FAILURES) { cooldownUntil = Date.now() + COOLDOWN_MS; failures = 0; }
          // Take the rejected code back off the cart and restore whatever was honoured before, so
          // it can't reach checkout and the shopper keeps their earlier saving.
          return postDiscounts(before).then(function (clean) {
            painted = true;
            paint(clean, prevSubtotal);
            showError(errGeneric());
          });
        }

        failures = 0;
        painted = true;
        paint(cart, prevSubtotal);

        // A shipping code reports applicable:true but allocates nothing until checkout has an
        // address, so it produces no cart-level discount. Say so, rather than showing Rs. 0.
        if (cartDiscount(cart) <= 0) { showError(MSG_SHIPPING); return; }

        announce(want + ' applied. You saved ' + money(codeSavings(cart, want)) + '.');
      })
      .catch(function (err) {
        showError(err && err.soft ? err.soft : ERR_NETWORK);
      })
      .then(function () {
        setBusy(false);
        // A failed request leaves a ticket in its "Applying…" state; repaint it from the last cart.
        if (!painted && lastCart) renderList(lastCart);
      });
  }

  function remove(code) {
    if (inFlight) return;
    clearError();
    setBusy(true);
    var prevSubtotal = null;
    getCart()
      .then(function (cart) {
        prevSubtotal = cart.items_subtotal_price;
        var keep = code
          ? appliedCodes(cart).filter(function (c) { return c.toUpperCase() !== String(code).toUpperCase(); })
          : [];
        return postDiscounts(keep);
      })
      .then(function (cart) {
        // An explicit remove always clears the Shiprocket hand-off, even on an emptied cart.
        if (!applicableCodes(cart).length) writeCookie(null);
        paint(cart, prevSubtotal);
        announce('Coupon removed.');
      })
      .catch(function () { showError(ERR_NETWORK); })
      .then(function () { setBusy(false); });
  }

  // clipboard.writeText needs a secure context and can be blocked outright, so fall back to a
  // throwaway textarea + execCommand rather than leaving the button silently dead.
  function copyCode(btn) {
    var code = btn.getAttribute('data-coupon-copy') || '';
    var done = function () {
      btn.classList.add('is-copied');
      announce('Code ' + code + ' copied.');
      setTimeout(function () { btn.classList.remove('is-copied'); }, 1400);
    };
    if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(done, function () { legacyCopy(code, done); });
    } else {
      legacyCopy(code, done);
    }
  }

  function legacyCopy(text, done) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:absolute;left:-9999px;';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
      done();
    } catch (e) { /* clipboard unavailable; the code is visible on screen anyway */ }
  }

  // ── Wiring ───────────────────────────────────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    if (!e.target || typeof e.target.closest !== 'function') return;

    var copy = e.target.closest('[data-coupon-copy]');
    if (copy) { copyCode(copy); return; }

    var pick = e.target.closest('[data-coupon-pick]');
    if (pick) {
      var card = pick.closest('.hov-offer');
      if (card && !inFlight) {
        card.classList.add('is-busy');
        pick.textContent = 'Applying…';
        pendingFocus = { code: card.getAttribute('data-code'), surface: surfaceOf(card) };
      }
      apply(pick.getAttribute('data-coupon-pick'));
      return;
    }

    if (e.target.closest('[data-coupon-apply]')) {
      var preview = e.target.closest('.hov-cart__coupon-preview') ||
        document.querySelector('.hov-cart__coupon-preview');
      var input = preview && preview.querySelector('[data-coupon-input]');
      apply(input ? input.value : '');
      return;
    }

    var rm = e.target.closest('[data-coupon-remove]');
    if (rm) {
      var rmCard = rm.closest('.hov-offer');
      if (rmCard && !inFlight) pendingFocus = { code: rmCard.getAttribute('data-code'), surface: surfaceOf(rmCard) };
      remove(rm.getAttribute('data-coupon-remove') || null);
      return;
    }

    var toggle = e.target.closest('[data-coupon-list-toggle]');
    if (toggle) {
      // Scoped to its own widget: the drawer's host must never be read or hidden from here.
      var scope = toggle.closest('.hov-cart__coupon-preview');
      var host = scope && scope.querySelector('[data-coupon-list]');
      if (!host) return;
      var openNow = host.hasAttribute('hidden');
      host.hidden = !openNow;
      toggle.setAttribute('aria-expanded', String(openNow));
    }
  });

  // The drawer's input sits inside form.ajaxcart (snippets/ajax-cart-template.liquid), so Enter
  // would implicitly submit that form and navigate to /cart. Apply the code instead.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || e.isComposing || !e.target || typeof e.target.matches !== 'function') return;
    if (!e.target.matches('[data-coupon-input]')) return;
    e.preventDefault();
    apply(e.target.value);
  });

  function sync() { getCart().then(render).catch(function () {}); }

  sync();

  // The theme's cart bus. assets/ajax-cart.js.liquid passes the fresh cart in e.detail.cart, so the
  // tickets repaint from it with no extra fetch and no stale state. Senders without a payload
  // (the /cart section re-render) get an immediate repaint from the last cart, then a fetch.
  document.addEventListener('cart:updated', function (e) {
    var c = e && e.detail && e.detail.cart;
    if (c && typeof c === 'object' && 'item_count' in c) { render(c); return; }
    if (lastCart) renderList(lastCart);
    sync();
  });

  window.hovCoupons = { apply: apply, remove: remove, sync: sync };
})();
